import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AlertTriangle, PenTool, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useDeal as useDealContext } from '@/contexts/deal-context';
import { createDefaultItems, ensureRequiredChecks, enforceVerificationOrder } from '@/hooks/use-deals';
import type { Deal } from '@/hooks/use-deals';
import { useFdicSearch } from '@/hooks/use-fdic';
import { useGleifExtractLei, useGleifSearch } from '@/hooks/use-gleif';
import type { GleifRecordData } from '@/hooks/use-gleif';
import { useTradeSearch } from '@/hooks/use-trade';
import type {
  VerificationDocumentProps,
  VerificationItem,
  GleifCheck,
  SignableNote,
} from './types';
import { SortableItem } from './components/sortable-item';
import { useVerificationAutosave } from './use-verification-autosave';

export function VerificationDocument({ deal }: VerificationDocumentProps): JSX.Element {
  const { user } = useUser();
  const userId = user?.id;
  const [items, setItems] = useState<VerificationItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const autoVerificationAttemptedRef = useRef<Set<string>>(new Set());
  const { save: onSave, setBaseBody, conflictDeal } = useVerificationAutosave(deal.id);

  const { mutateAsync: searchTradeAsync, isPending: isSearchingTrade } = useTradeSearch();
  const { mutateAsync: searchFdicAsync, isPending: isSearchingFdic } = useFdicSearch();
  const {
    mutateAsync: searchGleifAsync,
    isPending: isSearchingGleif,
  } = useGleifSearch();
  const { mutateAsync: extractLei } = useGleifExtractLei();
  const isSearching = isSearchingTrade || isSearchingFdic || isSearchingGleif;

  const loadDocument = useCallback((source: Deal): void => {
    let initialItems: VerificationItem[] = [];

    if (source.verification_document?.body) {
      try {
        const savedItems = JSON.parse(source.verification_document.body);
        if (Array.isArray(savedItems) && savedItems.length > 0) {
          initialItems = savedItems;
        }
      } catch {
        // If parsing fails, create default items
      }
    }

    // If no items were loaded, create defaults
    if (initialItems.length === 0) {
      initialItems = createDefaultItems(source);
    }

    // Apply guarantees to the initial items
    const guaranteedItems = enforceVerificationOrder(
      source,
      ensureRequiredChecks(source, initialItems),
    );

    setBaseBody(source.verification_document?.body);
    setItems(guaranteedItems);

    // Save the guaranteed items if they're different from what was stored
    if (JSON.stringify(guaranteedItems) !== source.verification_document?.body) {
      onSave(guaranteedItems);
    }
  }, [onSave, setBaseBody]);

  // Initialize items only once
  useEffect(() => {
    if (isInitialized)
      return;
    loadDocument(deal);
    setIsInitialized(true);
  }, [deal, isInitialized, loadDocument]);

  // Let other panels (e.g. contacts) add checks to this open document
  const { registerVerificationEditor } = useDealContext();
  useEffect(() => {
    if (!isInitialized || conflictDeal)
      return;
    return registerVerificationEditor((update) => {
      setItems((currentItems) => {
        const guaranteedItems = enforceVerificationOrder(
          deal,
          ensureRequiredChecks(deal, update(currentItems)),
        );
        onSave(guaranteedItems);
        return guaranteedItems;
      });
    });
  }, [conflictDeal, deal, isInitialized, onSave, registerVerificationEditor]);

  const handleVerifyOFAC = useCallback(async (id: string): Promise<void> => {
    const item = items.find((i) => i.id === id && i.type === 'ofac');
    if (!item || item.type !== 'ofac' || !item.entityName)
      return;

    try {
      const data = await searchTradeAsync(item.entityName);
      setItems((currentItems) => {
        const updatedItems = currentItems.map((i: VerificationItem) =>
          i.id === id && i.type === 'ofac'
            ? {
                ...i,
                searchResults: data,
                verified: true,
                verifiedBy: {
                  userId: userId!,
                  name: user?.fullName || 'Unknown User',
                  timestamp: new Date().toISOString(),
                },
              }
            : i,
        );
        const guaranteedItems = enforceVerificationOrder(
          deal,
          ensureRequiredChecks(deal, updatedItems),
        );
        onSave(guaranteedItems);
        return guaranteedItems;
      });
      toast.success('OFAC check completed and saved');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run OFAC check';
      toast.error(message);
    }
  }, [deal, items, onSave, searchTradeAsync, user?.fullName, userId]);

  const handleVerifyFDIC = useCallback(async (id: string): Promise<void> => {
    const item = items.find((i) => i.id === id && i.type === 'fdic');
    if (!item || item.type !== 'fdic' || !item.certNumber)
      return;

    try {
      const data = await searchFdicAsync(item.certNumber);
      setItems((currentItems) => {
        const updatedItems = currentItems.map((i: VerificationItem) => {
          // Update FDIC check item
          if (i.id === id && i.type === 'fdic') {
            return {
              ...i,
              searchResults: data,
              verified: true,
              verifiedBy: {
                userId: userId!,
                name: user?.fullName || 'Unknown User',
                timestamp: new Date().toISOString(),
              },
            };
          }

          // Update signable notes if we have valid FDIC results
          if (typeof data.results !== 'string') {
            const results = data.results;
            const orgEligibilityString = `FDIC Results: FDIC #${item.certNumber} (${results.basic_info.name})`;
            // Update org eligibility note
            if (i.id === 'org-eligibility' && i.type === 'note' && !i.content.includes(orgEligibilityString)) {
              return {
                ...i,
                content: `${orgEligibilityString}\n${i.content}`,
              };
            }

            const emailEligibilityString = `FDIC Results: FDIC #${item.certNumber} (${results.basic_info.website})`;
            // Update email eligibility note
            if (i.id === 'email-eligibility' && i.type === 'note' && !i.content.includes(emailEligibilityString)) {
              return {
                ...i,
                content: `${emailEligibilityString}\n${i.content}`,
              };
            }
            const orgAddressStringAddress = [
              results.location.address,
              results.location.address2,
              results.location.city,
              results.location.state,
              results.location.zip,
            ].filter(Boolean).join(', ');
            const orgAddressString = `FDIC Results: FDIC #${item.certNumber} (${orgAddressStringAddress})`;
            // Update org address note
            if (i.id === 'org-address' && i.type === 'note' && !i.content.includes(orgAddressString)) {
              return {
                ...i,
                content: `${orgAddressString}\n${i.content}`,
              };
            }
          }

          return i;
        });
        const guaranteedItems = enforceVerificationOrder(
          deal,
          ensureRequiredChecks(deal, updatedItems),
        );
        onSave(guaranteedItems);
        return guaranteedItems;
      });
      toast.success('FDIC check completed and saved');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run FDIC check';
      toast.error(message);
    }
  }, [deal, items, onSave, searchFdicAsync, user?.fullName, userId]);

  const handleVerifyGLEIF = useCallback(async (id: string): Promise<void> => {
    const item = items.find((i) => i.id === id && i.type === 'gleif');
    if (!item || item.type !== 'gleif' || item.notApplicable) {
      return;
    }
    if (!item.lei) {
      toast.error('Enter an LEI before verifying');
      return;
    }

    try {
      const data = await searchGleifAsync(item.lei);
      setItems((currentItems) => {
        const updatedItems = currentItems.map((entry) => {
          if (entry.id !== id || entry.type !== 'gleif')
            return entry;
          const results = data.results;
          const isVerified = results !== 'No results';
          const resolvedName = isVerified && typeof results !== 'string'
            ? results.data?.attributes.entity.legalName?.name || entry.entityName
            : entry.entityName;
          return {
            ...entry,
            entityName: resolvedName,
            searchResults: results,
            verified: isVerified,
            verifiedBy: isVerified
              ? {
                  userId: userId ?? 'system',
                  name: user?.fullName || 'Unknown User',
                  timestamp: new Date().toISOString(),
                }
              : undefined,
          };
        });
        const guaranteedItems = enforceVerificationOrder(
          deal,
          ensureRequiredChecks(deal, updatedItems),
        );
        onSave(guaranteedItems);
        return guaranteedItems;
      });
      toast.success('GLEIF check completed and saved');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run GLEIF check';
      toast.error(message);
    }
  }, [deal, items, onSave, searchGleifAsync, user?.fullName, userId]);

  const handleSelectGleifNameSearchResult = useCallback((id: string, record: GleifRecordData): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((entry) => {
        if (entry.id !== id || entry.type !== 'gleif')
          return entry;
        return {
          ...entry,
          lei: record.attributes.lei,
          entityName: record.attributes.entity.legalName?.name || entry.entityName,
          searchResults: { data: record },
          verified: true,
          verifiedBy: {
            userId: userId ?? 'system',
            name: user?.fullName || 'Unknown User',
            timestamp: new Date().toISOString(),
          },
        };
      });
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
    toast.success('GLEIF record selected and verified');
  }, [deal, onSave, user?.fullName, userId]);

  const handleResetGleif = useCallback((id: string): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((entry) => {
        if (entry.id !== id || entry.type !== 'gleif')
          return entry;
        return {
          ...entry,
          lei: '',
          searchResults: null,
          verified: false,
          verifiedBy: undefined,
          signatures: [],
        };
      });
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
    toast.info('GLEIF check reset');
  }, [deal, onSave]);

  const handleSetGleifNotApplicable = (id: string, notApplicable: boolean): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) => {
        if (item.id !== id || item.type !== 'gleif') {
          return item;
        }
        // When marking as N/A, treat it like a verification action with creator signature
        if (notApplicable) {
          const alreadySignedAsCreator = item.signatures.some(
            (s) => s.userId === userId && s.role === 'creator',
          );
          return {
            ...item,
            notApplicable: true,
            verified: true,
            verifiedBy: {
              userId: userId ?? 'system',
              name: user?.fullName || 'Unknown User',
              timestamp: new Date().toISOString(),
            },
            signatures: alreadySignedAsCreator
              ? item.signatures
              : [
                  ...item.signatures,
                  {
                    userId: userId ?? 'system',
                    name: user?.fullName || 'Unknown User',
                    role: 'creator' as const,
                    timestamp: new Date().toISOString(),
                  },
                ],
          };
        }
        // When un-marking N/A, clear verification state
        return {
          ...item,
          notApplicable: false,
          verified: false,
          verifiedBy: undefined,
          signatures: [],
        };
      });

      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems, true);
      return guaranteedItems;
    });
  };

  const handleSignNote = (id: string, role: 'creator' | 'reviewer'): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) => {
        if (item.id !== id)
          return item;

        // All verification items (note, ofac, fdic) have signatures
        const existingSignature = item.signatures.find(
          (s) => s.userId === userId && s.role === role,
        );

        if (existingSignature) {
          return {
            ...item,
            signatures: item.signatures.filter(
              (s) => !(s.userId === userId && s.role === role),
            ),
          };
        }

        return {
          ...item,
          signatures: [
            ...item.signatures,
            {
              userId: userId!,
              name: user?.fullName || 'Unknown User',
              role,
              timestamp: new Date().toISOString(),
            },
          ],
        };
      });

      // Apply guarantees
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
  };

  const handleUpdateNoteContent = (id: string, content: string): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) =>
        item.id === id && item.type === 'note'
          ? { ...item, content }
          : item,
      );
      // Apply guarantees and save
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
  };

  const handleUpdateNoteTitle = (id: string, title: string): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) =>
        item.id === id && item.type === 'note'
          ? { ...item, title }
          : item,
      );
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
  };

  const handleUpdateEntityName = (id: string, entityName: string): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) =>
        item.id === id && (item.type === 'ofac' || item.type === 'fdic' || item.type === 'gleif')
          ? { ...item, entityName }
          : item,
      );
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
  };

  const handleUpdateCertNumber = (id: string, certNumber: string): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) =>
        item.id === id && item.type === 'fdic'
          ? { ...item, certNumber }
          : item,
      );
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
  };

  const handleUpdateLei = (id: string, lei: string): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) =>
        item.id === id && item.type === 'gleif'
          ? { ...item, lei }
          : item,
      );
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      return guaranteedItems;
    });
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 8,
      },
    }),
  );

  const [isDragging, setIsDragging] = useState(false);
  const [convertingNoteId, setConvertingNoteId] = useState<string | null>(null);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;

    if (!over || active.id === over.id)
      return;

    setItems((currentItems) => {
      const oldIndex = currentItems.findIndex((item) => item.id === active.id);
      const newIndex = currentItems.findIndex((item) => item.id === over.id);
      const reorderedItems = arrayMove(currentItems, oldIndex, newIndex);

      // Apply guarantees to ensure correct ordering
      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, reorderedItems),
      );

      // Save with immediate flag since this is a user action
      onSave(guaranteedItems, true);

      // Return the guaranteed items to update the UI
      return guaranteedItems;
    });
  };

  const handleBulkSignAsReviewer = (): void => {
    setItems((currentItems) => {
      const updatedItems = currentItems.map((item) => {
        // Skip if already signed by this user as reviewer
        if (item.signatures.some((s) => s.userId === userId && s.role === 'reviewer')) {
          return item;
        }

        // Only sign items that are either verified (for OFAC/FDIC/GLEIF) or are notes
        if ((item.type === 'ofac' || item.type === 'fdic' || item.type === 'gleif') && !item.verified) {
          return item;
        }

        return {
          ...item,
          signatures: [
            ...item.signatures,
            {
              userId: userId ?? 'system',
              name: user?.fullName || 'Unknown User',
              role: 'reviewer' as const,
              timestamp: new Date().toISOString(),
            },
          ],
        };
      });

      const guaranteedItems = enforceVerificationOrder(
        deal,
        ensureRequiredChecks(deal, updatedItems),
      );
      onSave(guaranteedItems);
      toast.success('Bulk signed all eligible items as reviewer');
      return guaranteedItems;
    });
  };

  const handleConvertNoteToGleif = async (noteId: string): Promise<void> => {
    const note = items.find((item): item is SignableNote => item.id === noteId && item.type === 'note');
    if (!note) {
      toast.error('Note not found');
      return;
    }

    setConvertingNoteId(noteId);
    try {
      const extraction = await extractLei({
        content: note.content,
        title: note.title,
      });

      if (!extraction.lei) {
        throw new Error('Unable to detect an LEI in this note');
      }

      const gleifResponse = await searchGleifAsync(extraction.lei);
      if (!gleifResponse.results || gleifResponse.results === 'No results') {
        throw new Error('No GLEIF record found for the extracted LEI');
      }

      const gleifResults = gleifResponse.results;
      const newItem: GleifCheck = {
        id: `gleif-${Date.now()}`,
        type: 'gleif',
        entityName: gleifResults.data?.attributes.entity.legalName?.name || note.title,
        lei: extraction.lei,
        searchResults: gleifResults,
        verified: true,
        verifiedBy: {
          userId: userId ?? 'system',
          name: user?.fullName || 'Unknown User',
          timestamp: new Date().toISOString(),
        },
        signatures: [],
      };

      setItems((currentItems) => {
        const noteIndex = currentItems.findIndex((item) => item.id === noteId);
        if (noteIndex === -1) {
          return currentItems;
        }
        const updatedItems = [
          ...currentItems.slice(0, noteIndex + 1),
          newItem,
          ...currentItems.slice(noteIndex + 1),
        ];
        const guaranteedItems = enforceVerificationOrder(
          deal,
          ensureRequiredChecks(deal, updatedItems),
        );
        onSave(guaranteedItems, true);
        toast.success('GLEIF widget created from note');
        return guaranteedItems;
      });
    } catch (error) {
      let message = 'Failed to convert note to GLEIF widget';
      if (error instanceof Error && error.message && error.message !== '[object Object]') {
        message = error.message;
      } else if (error && typeof error === 'object') {
        const maybeResponseError = (error as { response?: { data?: unknown } }).response?.data;
        if (typeof maybeResponseError === 'string') {
          message = maybeResponseError;
        } else if (maybeResponseError && typeof maybeResponseError === 'object' && 'error' in maybeResponseError) {
          const extracted = (maybeResponseError as { error?: unknown }).error;
          message = typeof extracted === 'string'
            ? extracted
            : JSON.stringify(extracted ?? maybeResponseError);
        } else {
          message = JSON.stringify(error);
        }
      } else if (typeof error === 'string') {
        message = error;
      }
      toast.error(message);
    } finally {
      setConvertingNoteId(null);
    }
  };

  useEffect(() => {
    if (!isInitialized)
      return;

    const tasks: Array<() => Promise<void>> = [];

    for (const item of items) {
      if (item.signatures.length > 0)
        continue;

      if (autoVerificationAttemptedRef.current.has(item.id))
        continue;

      if (item.type === 'ofac' && item.entityName) {
        autoVerificationAttemptedRef.current.add(item.id);
        tasks.push(async () => {
          await handleVerifyOFAC(item.id);
        });
      } else if (item.type === 'fdic' && item.certNumber) {
        autoVerificationAttemptedRef.current.add(item.id);
        tasks.push(async () => {
          await handleVerifyFDIC(item.id);
        });
      } else if (item.type === 'gleif' && !item.notApplicable && item.lei) {
        autoVerificationAttemptedRef.current.add(item.id);
        tasks.push(async () => {
          await handleVerifyGLEIF(item.id);
        });
      }
    }

    if (tasks.length === 0)
      return;

    void Promise.all(tasks.map(async (task) => {
      await task();
    }));
  }, [isInitialized, items, handleVerifyOFAC, handleVerifyFDIC, handleVerifyGLEIF]);

  useEffect(() => {
    autoVerificationAttemptedRef.current = new Set();
  }, [deal.id]);

  return (
    <div className="flex h-full max-h-[600px] flex-col overflow-hidden bg-background">
      <div className="flex items-center justify-between border-b p-4">
        <h3 className="text-sm font-bold">Verification Document</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleBulkSignAsReviewer}
            className="gap-2"
          >
            <PenTool className="size-4" />
            Sign as Reviewer
          </Button>
          <Button
            variant="default"
            disabled={!!conflictDeal}
            onClick={() => {
              // Immediate save when clicking the button
              const guaranteedItems = enforceVerificationOrder(
                deal,
                ensureRequiredChecks(deal, items),
              );
              onSave(guaranteedItems, true);
            }}
            className="gap-2"
          >
            <Save className="size-4" />
            Save
          </Button>
        </div>
      </div>
      {conflictDeal && (
        <div className="flex items-start gap-3 border-b border-destructive/30 bg-destructive/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <div className="flex-1 space-y-2">
            <p className="font-medium text-destructive">Changes here are no longer being saved</p>
            <p>
              This document was changed in another tab or by another user after you opened it,
              so your latest change was not saved. Copy anything you need to keep, then load the latest version.
            </p>
            <Button size="sm" variant="destructive" onClick={() => loadDocument(conflictDeal)}>
              Load latest version
            </Button>
          </div>
        </div>
      )}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableItem
                key={item.id}
                id={item.id}
                item={item}
                onVerifyOFAC={handleVerifyOFAC}
                onVerifyFDIC={handleVerifyFDIC}
                onVerifyGLEIF={handleVerifyGLEIF}
                onSelectGleifNameSearchResult={handleSelectGleifNameSearchResult}
                onResetGleif={handleResetGleif}
                onSetGleifNotApplicable={handleSetGleifNotApplicable}
                onUpdateNoteContent={handleUpdateNoteContent}
                onUpdateNoteTitle={handleUpdateNoteTitle}
                onUpdateEntityName={handleUpdateEntityName}
                onUpdateCertNumber={handleUpdateCertNumber}
                onUpdateLei={handleUpdateLei}
                onSignNote={handleSignNote}
                isAnyDragging={isDragging}
                onDelete={(id) => {
                  setItems((currentItems) => {
                    const updatedItems = currentItems.filter((item) => item.id !== id);
                    const guaranteedItems = enforceVerificationOrder(
                      deal,
                      ensureRequiredChecks(deal, updatedItems),
                    );
                    onSave(guaranteedItems);
                    return guaranteedItems;
                  });
                }}
                isSearching={isSearching}
                userId={userId}
                items={items}
                setItems={(newItems) => {
                  const guaranteedItems = enforceVerificationOrder(
                    deal,
                    ensureRequiredChecks(deal, newItems),
                  );
                  setItems(guaranteedItems);
                  onSave(guaranteedItems);
                }}
                deal={deal}
                onConvertNoteToGleif={handleConvertNoteToGleif}
                convertingNoteId={convertingNoteId}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}
